import {
  QueryFunctionContext,
  UseInfiniteQueryOptions
} from '@tanstack/react-query';

import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, AutoRFQApi } from '@setvi/shared/enums';

import { RfqFilters, RfqItemsResponse } from './Types';

export enum AutoRFQQueryKey {
  GET_ALL = 'GET_ALL_AUTO_RFQ',
  GET_SINGLE = 'GET_SINGLE_AUTO_RFQ',
  GET_ITEMS = 'GET_ITEMS_AUTO_RFQ',
  GET_MATCHED_ITEMS = 'GET_MATCHED_ITEMS_AUTO_RFQ',
  GENERATE_CSV = 'GENERATE_CSV_AUTO_RFQ'
}

export const getRfqsQuery = (params: RfqFilters) => ({
  queryKey: [AutoRFQQueryKey.GET_ALL, params],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetAll,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_INTELLIGENCE_API,
      params
    })
});

export const getRfqQuery = (id: string | number) => ({
  queryKey: [AutoRFQQueryKey.GET_SINGLE, id],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetSingle.replace(':id', id.toString()),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_INTELLIGENCE_API
    })
});

export const getRfqItemsQuery = (
  id: string | number,
  params: RfqFilters
): UseInfiniteQueryOptions<RfqItemsResponse> => ({
  queryKey: [AutoRFQQueryKey.GET_ITEMS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: `${AutoRFQApi.GetItems.replace(':id', id?.toString())}`,
      method: AxiosMethods.GET,
      params: {
        ...params,
        page: data.pageParam || params.page
      },
      baseURL: process.env.REVOPS_INTELLIGENCE_API
    }),
  getNextPageParam: (lastPage: RfqItemsResponse) => {
    const total = lastPage?.total;
    const currPage = lastPage.page || 1;
    const currSize = lastPage.limit || 10;

    return currPage * currSize >= total ? undefined : currPage + 1;
  },
  refetchOnWindowFocus: false,
  cacheTime: 0
});

export const getRfqMatchedItemsQuery = (ids: string[]) => ({
  queryKey: [AutoRFQQueryKey.GET_MATCHED_ITEMS, ids],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetMatchedItems,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_INTELLIGENCE_API,
      body: ids
    })
});

export const generateRfqCSVQuery = (id: string | number) => ({
  queryKey: [AutoRFQQueryKey.GENERATE_CSV, id],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GenerateCSV.replace(':id', id.toString()),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_INTELLIGENCE_API,
      responseType: 'blob', // Treat response as a binary file
      headers: {
        Accept: 'text/csv' // Expect CSV response
      }
    })
});
